// Carmarthen, Ammanford, Gorseinon and Pontardawe.
// Council pages checked 29 October 2026. Do not add street addresses,
// carrier counts, or registration numbers.
//
// Carmarthenshire (HTTP 200 that day):
//   Recycling centres hub lists Nantycaws (Carmarthen), Trostre (Llanelli),
//   Wernddu (Ammanford) and Whitland. The hub said appointments are no longer
//   required. Proof of residency still is.
//   https://www.carmarthenshire.gov.wales/council-services/recycling-bins-and-litter/recycling-centres/
//   Nantycaws: .../recycling-centres/nantycaws-carmarthen/
//   Wernddu:   .../recycling-centres/wernddu-ammanford/
//   Fly-tipping page and the myaccount report form both returned HTTP 200.
//
// Neath Port Talbot recycling centres page (title confirmed that day) lists
// Briton Ferry, Cymmer and Lower Cwmtwrch (Powys), and says you must book a slot:
//   https://www.npt.gov.uk/bins-and-recycling/recycling-and-sustainability/recycling-centres/
// Fly-tipping: https://www.npt.gov.uk/parking-roads-and-travel/problems-with-roads-and-streets/fly-tipping/
//
// Swansea (Gorseinon, Gowerton, Loughor, Penllergaer, and Clydach):
//   Garngoch is the recycling centre in Gorseinon. Same hub and fly-tipping
//   form as the Swansea page. A direct fetch of swansea.gov.uk returned a bot
//   check on 29 October 2026, so no bulky-waste price was copied from it.
//   https://www.swansea.gov.uk/recyclingcentres
//   https://www.swansea.gov.uk/reportflytipping

import { EA_CARRIER_REGISTER, NRW_CARRIER_REGISTER } from './site';

const link = (text, href) => ({ t: text, href });

const registerCheck = (after = '') => [
  'Registered in England? Check the ',
  link('Environment Agency register', EA_CARRIER_REGISTER),
  '. Registered in Wales? Check the ',
  link('Natural Resources Wales register', NRW_CARRIER_REGISTER),
  after ? `. ${after}` : '.',
];

const CARMS_HUB = 'https://www.carmarthenshire.gov.wales/council-services/recycling-bins-and-litter/recycling-centres/';
const NANTYCAWS = 'https://www.carmarthenshire.gov.wales/council-services/recycling-bins-and-litter/recycling-centres/nantycaws-carmarthen/';
const WERNDDU = 'https://www.carmarthenshire.gov.wales/council-services/recycling-bins-and-litter/recycling-centres/wernddu-ammanford/';
const CARMS_FLY = 'https://www.carmarthenshire.gov.wales/council-services/recycling-bins-and-litter/litter/fly-tipping/';
const CARMS_REPORT = 'https://myaccount.carmarthenshire.gov.wales/en/service/Report_fly_tipping_process';
const NPT_CENTRES = 'https://www.npt.gov.uk/bins-and-recycling/recycling-and-sustainability/recycling-centres/';
const NPT_FLY = 'https://www.npt.gov.uk/parking-roads-and-travel/problems-with-roads-and-streets/fly-tipping/';
const SWANSEA_CENTRES = 'https://www.swansea.gov.uk/recyclingcentres';
const SWANSEA_FLY = 'https://www.swansea.gov.uk/reportflytipping';

export const NEW_AREAS = [
  {
    slug: 'carmarthen',
    name: 'Carmarthen',
    path: '/areas/carmarthen',
    adminArea: 'Carmarthenshire',
    title: 'Rubbish removal in Carmarthen | The WasteGame',
    description: 'Post a Carmarthen rubbish or house clearance job for free. Registered waste carriers send prices. Pay by card only after the job is marked complete.',
    h1: 'Rubbish removal in Carmarthen',
    eyebrow: 'Carmarthen, Carmarthenshire',
    card: 'Terraces in town, farm lanes towards Llanstephan and Bronwydd, and a run to Nantycaws if you can take it yourself.',
    intro: [
      'Carmarthen jobs range from a terrace in town to a farmhouse lane out towards Llanstephan or Bronwydd. This page is for people in and around Carmarthen who want registered waste carriers to send them a price, without paying to ask. Posting is free. It does not book a van, and it does not mean anyone will send a price. If carriers want the work, they send their own prices. You pick one, or you pick none.',
      'The WasteGame is new. It connects you with registered waste carriers. It does not collect the waste itself, and it holds no waste carrier licence. You pay by card only if you accept a price and the job is marked complete. There is no customer fee.',
    ],
    howLead: 'For the price, say if it is a rural lane, a farm track or a steep drive, and whether a van can turn. Name the village if it is not the town. Llanstephan and Bronwydd are not the same journey as a street in Carmarthen.',
    jobs: [
      {
        icon: '🛋️',
        title: 'Furniture',
        text: 'Sofas and wardrobes from town terraces, and pieces that will not fit in a car for the trip to Nantycaws. Say the floor and whether the lane is too narrow for a van to turn.',
      },
      {
        icon: '🌿',
        title: 'Garden waste',
        text: 'Hedges, soil and old timber from houses on the edge of town and from gardens out towards Bronwydd. Say if it is bagged at the gate or still up a farm track.',
      },
      {
        icon: '🛏️',
        title: 'Mattresses',
        text: 'Say the size and how many, and whether any are wet. A single mattress from a box room is a different job from clearing several after a house move.',
      },
      {
        icon: '🧱',
        title: 'Rubble',
        text: 'A few bags from your own house: a path, a fireplace, a small bathroom job. Give a bag count. This is not a booking for a building site.',
      },
      {
        icon: '🏠',
        title: 'House and end-of-tenancy clearance',
        text: 'A family house or a rented place being handed back. List the rooms and the bulky items, and say what stays. A clearance of one room is not an empty farmhouse.',
      },
    ],
    localTitle: 'Nantycaws and fly-tipping in Carmarthenshire',
    local: [
      [
        'Carmarthenshire County Council runs household recycling centres, including ',
        link('Nantycaws near Carmarthen', NANTYCAWS),
        '. The county also lists Trostre, Wernddu and Whitland on its ',
        link('recycling centres page', CARMS_HUB),
        '. Check that page before you load the car. When this page was written the council said appointments were no longer required. You still need proof you live in Carmarthenshire.',
      ],
      [
        'Report dumped waste on the council’s ',
        link('fly-tipping page', CARMS_FLY),
        ' or the ',
        link('online report form', CARMS_REPORT),
        '. These are the same links as the Llanelli page. Do not move the waste yourself. ',
        ...registerCheck('The WasteGame asks carriers for a waste carrier registration number. That is not a promise that a particular carrier will take your job.'),
      ],
    ],
    faqs: [
      {
        q: 'Is it free to post a rubbish job in Carmarthen?',
        a: 'Yes. Creating the job costs nothing. You only pay a carrier if you accept their price, and on a priced job you pay that price by card after the job is marked complete. The WasteGame does not add a customer fee.',
      },
      {
        q: 'Will someone come out to the villages?',
        a: 'Carriers decide. Put the nearest village and postcode district so they can judge the distance. Posting does not book a van, and it does not mean anyone will send a price.',
      },
      {
        q: 'Can I take it to Nantycaws myself?',
        a: [
          'Often yes, if you live in Carmarthenshire and the site accepts it. Start with the ',
          link('Nantycaws page', NANTYCAWS),
          ' and the ',
          link('recycling centres page', CARMS_HUB),
          ' for what to bring. A collection is for waste you cannot take there yourself.',
        ],
      },
      {
        q: 'How do I check the carrier?',
        a: [
          ...registerCheck('The WasteGame does not publish a registration number on this page. A registration is not a promise that someone will take your job.'),
        ],
      },
    ],
    nearby: ['llanelli', 'ammanford'],
  },
  {
    slug: 'ammanford',
    name: 'Ammanford',
    path: '/areas/ammanford',
    adminArea: 'Carmarthenshire',
    title: 'Rubbish removal in Ammanford | The WasteGame',
    description: 'Post an Ammanford rubbish removal job for free. Registered waste carriers send prices. Pay by card only after the job is marked complete. No customer fee.',
    h1: 'Rubbish removal in Ammanford',
    eyebrow: 'Ammanford, Amman Valley',
    card: 'Ammanford, Tycroes, Glanaman and Brynamman, with Wernddu as the local recycling centre if you can drive it there.',
    intro: [
      'For Ammanford, Tycroes, Glanaman, Brynamman and the Amman Valley, the same rules apply: posting is free, carriers choose whether to send a price, and you pay by card only when the job is done. It does not book a van, and it does not mean anyone will send a price.',
      'The WasteGame is new. It connects you with registered waste carriers and does not collect the waste itself. There is no customer fee on top of a price you accept.',
    ],
    howLead: 'Name the village. Ammanford town, Tycroes, Glanaman and Brynamman are not the same stop for a van. Say if the street is on a hill and whether the waste is already at the front.',
    jobs: [
      {
        icon: '🛋️',
        title: 'Furniture',
        text: 'Sofas and beds from valley houses that will not fit in a family car for the trip to Wernddu. Say the floor and whether the item comes apart.',
      },
      {
        icon: '🌿',
        title: 'Garden waste',
        text: 'Branches, soil and old fence panels from gardens in Tycroes, Glanaman and out towards Brynamman. Say if the pile is bagged or still at the top of the garden.',
      },
      {
        icon: '🛏️',
        title: 'Mattresses',
        text: 'Say the size and how many. A damp mattress should be described that way. One mattress and a house full of them are different jobs.',
      },
      {
        icon: '🧱',
        title: 'Rubble',
        text: 'Bags of brick, tile or soil from your own house. Give a count. Trade waste and demolition are not what this page is for.',
      },
      {
        icon: '🏠',
        title: 'House and end-of-tenancy clearance',
        text: 'A rented house being handed back, or a family home being emptied. List the rooms and the bulky pieces. “Full house” without a list is hard to price.',
      },
    ],
    localTitle: 'Wernddu and fly-tipping in Carmarthenshire',
    local: [
      [
        'Carmarthenshire County Council’s ',
        link('Wernddu recycling centre', WERNDDU),
        ' is in Ammanford. The other county sites, including Nantycaws and Trostre, are on the ',
        link('recycling centres page', CARMS_HUB),
        '. Check that page before you load the car. When this page was written the council said appointments were no longer required. Take proof you live in Carmarthenshire.',
      ],
      [
        'Fly-tipping goes on the Carmarthenshire ',
        link('fly-tipping page', CARMS_FLY),
        ' and the ',
        link('report form', CARMS_REPORT),
        '. Do not move the waste. ',
        ...registerCheck('The WasteGame does not collect waste and holds no waste carrier licence.'),
      ],
    ],
    faqs: [
      {
        q: 'Is it free to post an Ammanford job?',
        a: 'Yes. Posting is free. You pay by card only if you accept a price and the job is marked complete. There is no customer fee. Posting does not book a van.',
      },
      {
        q: 'Do you cover Tycroes, Glanaman and Brynamman?',
        a: 'You can post a job with the village and postcode district. A registered waste carrier then decides whether the journey is one they will price. This page does not promise a collection in every lane.',
      },
      {
        q: 'Can I take it to Wernddu myself?',
        a: [
          'Often yes. Read the ',
          link('Wernddu page', WERNDDU),
          ' for what the site accepts and who can enter. A collection is for waste you cannot take there.',
        ],
      },
      {
        q: 'How do I check the carrier?',
        a: [
          ...registerCheck('A registration is not a promise that someone will take an Ammanford job.'),
        ],
      },
    ],
    nearby: ['carmarthen', 'llanelli', 'swansea'],
  },
  {
    slug: 'gorseinon',
    name: 'Gorseinon',
    path: '/areas/gorseinon',
    adminArea: 'City and County of Swansea',
    title: 'Rubbish removal in Gorseinon and Gowerton | The WasteGame',
    description: 'Post a rubbish removal job in Gorseinon, Gowerton, Loughor or Penllergaer for free. Registered waste carriers send prices. Pay by card after the job is done.',
    h1: 'Rubbish removal in Gorseinon and Gowerton',
    eyebrow: 'Gorseinon and Gowerton, Swansea',
    card: 'Gorseinon, Gowerton, Loughor and Penllergaer. Garngoch is the nearest Swansea Council recycling centre.',
    intro: [
      'Gorseinon, Gowerton, Loughor and Penllergaer are in the City and County of Swansea. A sofa in a Gowerton terrace and a garage clear-out in Penllergaer are different jobs. This page is for people who want registered waste carriers to send a price, without paying to ask.',
      'Posting is free. It does not book a van, and it does not mean anyone will send a price. The WasteGame is new. You pay by card only after you accept a price and the job is marked complete. There is no customer fee. The WasteGame does not collect the waste itself.',
    ],
    howLead: 'Say which place: Gorseinon, Gowerton, Loughor or Penllergaer. Parking on a terraced street is not the same as a drive in Penllergaer. Say if the waste is upstairs.',
    jobs: [
      {
        icon: '🛋️',
        title: 'Furniture',
        text: 'Sofas and wardrobes from terraces in Gorseinon and Gowerton, and from houses in Loughor. Say the floor and whether it has to bend around a landing.',
      },
      {
        icon: '🌿',
        title: 'Garden waste',
        text: 'Hedges, soil and shed clear-outs, including gardens in Penllergaer. Say if a van can reach the gate or the pile is still at the back.',
      },
      {
        icon: '🛏️',
        title: 'Mattresses',
        text: 'Say the size and how many, and if any are wet. If you can move them yourself, check what Garngoch and the other Swansea sites accept before you drive.',
      },
      {
        icon: '🧱',
        title: 'Rubble',
        text: 'A small amount from your own house, bagged. Give a count. This page is not for commercial building waste.',
      },
      {
        icon: '🏠',
        title: 'House and end-of-tenancy clearance',
        text: 'A house or flat being emptied in Gorseinon, Gowerton or Loughor. List the rooms and the bulky items. Say what the next person is keeping.',
      },
    ],
    localTitle: 'Garngoch and Swansea fly-tipping',
    local: [
      [
        'These areas are in the City and County of Swansea. Swansea Council’s Garngoch recycling centre is the nearest tip. The council also runs Llansamlet, Clyne, Penlan and Tir John. Check ',
        link('the Swansea recycling centres page', SWANSEA_CENTRES),
        ' for what each site takes before you load the car.',
      ],
      [
        'Report dumped waste on ',
        link('Swansea Council’s fly-tipping form', SWANSEA_FLY),
        '. These are the same links as the Swansea page. Do not move the waste. ',
        ...registerCheck('The WasteGame asks carriers to be registered waste carriers. That is not a promise that someone will take your job.'),
      ],
    ],
    faqs: [
      {
        q: 'Is Gowerton on this page?',
        a: 'Yes. Gorseinon, Gowerton, Loughor and Penllergaer are all in the City and County of Swansea. Put the place name on the job so a carrier can judge the journey.',
      },
      {
        q: 'Is it free to post?',
        a: 'Yes. You pay by card only if you accept a price and the job is marked complete. There is no customer fee. Posting does not book a van, and it does not mean anyone will send a price.',
      },
      {
        q: 'Which tip is nearest?',
        a: [
          'Garngoch, for most of Gorseinon and the neighbouring villages. Confirm what it takes on ',
          link('Swansea’s recycling centres page', SWANSEA_CENTRES),
          '. A collection is for waste you cannot take there yourself.',
        ],
      },
      {
        q: 'How do I check the carrier?',
        a: [
          ...registerCheck('The WasteGame does not show a registration number on this page.'),
        ],
      },
    ],
    nearby: ['swansea', 'llanelli'],
  },
  {
    slug: 'pontardawe',
    name: 'Pontardawe',
    path: '/areas/pontardawe',
    adminArea: 'Neath Port Talbot',
    title: 'Rubbish removal in Pontardawe and the Swansea Valley | The WasteGame',
    description: 'Post a rubbish job in Pontardawe, Ystalyfera, Alltwen or Clydach for free. Registered waste carriers send prices. Pay by card only after the job is marked complete.',
    h1: 'Rubbish removal in Pontardawe and the Swansea Valley',
    eyebrow: 'Pontardawe, Swansea Valley',
    card: 'Pontardawe, Ystalyfera and Alltwen in Neath Port Talbot, and Clydach, which is in Swansea.',
    intro: [
      'Pontardawe, Ystalyfera and Alltwen are in Neath Port Talbot. Clydach is in the City and County of Swansea, a short way down the valley. Post a job with the village named. Posting is free. It does not book a van, and it does not mean anyone will send a price.',
      'The WasteGame is new. Registered waste carriers send their own prices if they want the work. You pay by card only after the job is marked complete. There is no customer fee. The WasteGame does not collect the waste itself and holds no waste carrier licence.',
    ],
    howLead: 'Say Pontardawe, Ystalyfera, Alltwen or Clydach. Valley parking and a steep street change the job. Clydach is a different council from Pontardawe, so the tip you would use yourself is different too.',
    jobs: [
      {
        icon: '🛋️',
        title: 'Furniture',
        text: 'Sofas and wardrobes from valley terraces. Say the floor and whether the street has room to stop. A photo of the stairs is more useful than a long description.',
      },
      {
        icon: '🌿',
        title: 'Garden waste',
        text: 'Soil, hedges and old timber from houses up the valley. Say if it is at the gate or still at the back where a van cannot go.',
      },
      {
        icon: '🛏️',
        title: 'Mattresses',
        text: 'Say the size and how many, and if any are damp. One mattress from Alltwen and a house clear-out in Ystalyfera should not be written up the same way.',
      },
      {
        icon: '🧱',
        title: 'Rubble',
        text: 'A few bags from a home repair. Give a count. Neath Port Talbot’s sites treat larger loads of building-type waste differently, so do not describe a driveway of rubble as “a bit of rubble”.',
      },
      {
        icon: '🏠',
        title: 'House and end-of-tenancy clearance',
        text: 'A house in Pontardawe or a place in Clydach being emptied. List the rooms and the bulky items. Say which village, because the council area is not the same for both.',
      },
    ],
    localTitle: 'Neath Port Talbot tips, and Swansea if you are in Clydach',
    local: [
      [
        'Pontardawe and Ystalyfera are in Neath Port Talbot. On 29 October 2026 the council’s recycling centres page listed Briton Ferry, Cymmer and Lower Cwmtwrch (Powys), and said you must book a slot. Lower Cwmtwrch is in Powys. Read ',
        link('the council’s recycling centres page', NPT_CENTRES),
        ' before you go. Fly-tipping in Neath Port Talbot goes on the ',
        link('council’s fly-tipping page', NPT_FLY),
        '.',
      ],
      [
        'Clydach is in Swansea, so Swansea’s sites and fly-tipping form apply there, not the Neath Port Talbot booking page. Use ',
        link('Swansea’s recycling centres page', SWANSEA_CENTRES),
        ' and ',
        link('Swansea’s fly-tipping form', SWANSEA_FLY),
        '. Do not move dumped waste. ',
        ...registerCheck(),
      ],
    ],
    faqs: [
      {
        q: 'Is it free to post a Pontardawe job?',
        a: 'Yes. You pay by card only if you accept a price and the job is marked complete. There is no customer fee. Posting does not book a van, and it does not mean anyone will send a price.',
      },
      {
        q: 'Do I need to book the tip?',
        a: [
          'For Neath Port Talbot sites, yes. The council says you must book a slot. The list of sites is on ',
          link('the recycling centres page', NPT_CENTRES),
          '. Clydach is in Swansea, so Swansea’s rules apply there instead.',
        ],
      },
      {
        q: 'Will someone come to Ystalyfera or Alltwen?',
        a: 'Only if a registered waste carrier chooses to price the job. Name the village. The WasteGame is new and does not promise a collection.',
      },
      {
        q: 'How do I check the carrier?',
        a: [
          ...registerCheck('A registration is not a promise of a collection in the Swansea Valley.'),
        ],
      },
    ],
    nearby: ['neath', 'swansea'],
  },
];
